import { onLanguageChange, t } from '../i18n';
import type { RoundResult } from '../net/protocol';
import type { SocketStatus } from '../net/socket';
import { formatMoney, formatMoneyParts, formatMultiplier } from '../util/format';

/**
 * The chrome around the scene: balance, results strip, connection state.
 *
 * Everything it writes is script-generated, so it keeps the arguments of its
 * last call and redraws from them when the language changes. Without that, a
 * player switching language mid-round would keep the old language's balance hint
 * until the next server message happened to refresh it.
 */
export class Hud {
  private readonly balance = required('balance');
  private readonly balanceAmount = child(this.balance, '.amount');
  private readonly balanceUnit = child(this.balance, '.unit');
  private readonly nickname = required('nickname');
  private readonly history = required('history');
  private readonly connection = required('connection');
  private readonly commit = required('commit');
  private readonly hintLimits = required('hint-limits');
  private readonly hintFair = required('hint-fair');

  private scale = 2;
  private symbol = '';

  /** The last values passed in, so a language change can redraw from them. */
  private limits: { minBet: string; maxBet: string; dailyMax?: string; dailyUsed?: string } | null =
    null;
  private commitments: { nonce: number; hash: string; nextNonce: number; nextHash: string } | null =
    null;
  private status: SocketStatus = 'connecting';
  private onSelectRound: ((nonce: number) => void) | null = null;

  constructor() {
    onLanguageChange(() => {
      if (this.limits) {
        this.setLimitsHint(
          this.limits.minBet,
          this.limits.maxBet,
          this.limits.dailyMax,
          this.limits.dailyUsed,
        );
      }
      if (this.commitments) {
        const c = this.commitments;
        this.setCommitments(c.nonce, c.hash, c.nextNonce, c.nextHash);
      }
      this.setConnection(this.status);
      this.retitleHistory();
    });
  }

  setPlayer(nickname: string, currency: string, scale: number, symbol: string): void {
    this.nickname.textContent = `${nickname} · ${currency}`;
    this.scale = scale;
    this.symbol = symbol;
  }

  /**
   * Writes the balance as two nodes, the figure and the unit, so the header can
   * give them different weight. The nodes are never replaced -- only their text
   * changes -- for the same reason the cash-out button's children are not: a
   * node swapped under a cursor eats the click that was landing on it.
   */
  setBalance(amount: string): void {
    const parts = formatMoneyParts(amount, this.scale, this.symbol);
    this.balanceAmount.textContent = parts.amount;
    this.balanceUnit.textContent = parts.unit;

    // A symbol goes before the figure, a code after it. Reordering the two nodes
    // rather than rewriting them keeps this a property of the currency and not of
    // the string.
    const unitFirst = this.balance.firstElementChild === this.balanceUnit;
    if (parts.unitFirst !== unitFirst) {
      this.balance.replaceChildren(
        ...(parts.unitFirst
          ? [this.balanceUnit, this.balanceAmount]
          : [this.balanceAmount, this.balanceUnit]),
      );
    }
    // A symbol sits against its figure -- "$10.00", not "$ 10.00" -- while a
    // three-letter code needs the space. Same rule as formatMoney's.
    this.balance.classList.toggle('symbol-first', parts.unitFirst);
  }

  setLimitsHint(minBet: string, maxBet: string, dailyMax?: string, dailyUsed?: string): void {
    this.limits = { minBet, maxBet, dailyMax, dailyUsed };

    const parts = [
      t('hud.betRange', {
        min: formatMoney(minBet, this.scale, this.symbol),
        max: formatMoney(maxBet, this.scale, this.symbol),
      }),
    ];
    if (dailyMax && dailyUsed) {
      parts.push(
        t('hud.dailyLimit', {
          used: formatMoney(dailyUsed, this.scale, this.symbol),
          max: formatMoney(dailyMax, this.scale, this.symbol),
        }),
      );
    }
    this.hintLimits.textContent = parts.join('  ·  ');
  }

  /**
   * Shows the commitment for the round being played and the one already sealed
   * for the round after it.
   *
   * This is on screen rather than buried in a menu on purpose: the commitment is
   * only worth anything if a player can see it before they bet.
   */
  setCommitments(nonce: number, commitHash: string, nextNonce: number, nextCommitHash: string): void {
    this.commitments = { nonce, hash: commitHash, nextNonce, nextHash: nextCommitHash };

    this.commit.textContent = t('hud.commitment', {
      nonce,
      hash: commitHash.slice(0, 24),
      nextNonce,
      nextHash: nextCommitHash.slice(0, 16),
    });
    this.hintFair.textContent = t('hud.fairness', { nonce });
  }

  setHistory(results: RoundResult[], onSelect: (nonce: number) => void): void {
    this.onSelectRound = onSelect;
    this.history.replaceChildren();
    for (const r of results) {
      this.history.appendChild(this.chip(r, onSelect));
    }
  }

  pushHistory(result: RoundResult, onSelect: (nonce: number) => void): void {
    this.onSelectRound = onSelect;
    this.history.prepend(this.chip(result, onSelect));

    while (this.history.children.length > 30) {
      this.history.lastElementChild?.remove();
    }
  }

  setConnection(status: SocketStatus): void {
    this.status = status;
    const text =
      status === 'connecting'
        ? t('conn.connecting')
        : status === 'reconnecting'
          ? t('conn.reconnecting')
          : status === 'closed'
            ? t('conn.closed')
            : '';
    this.connection.textContent = text;
    this.connection.classList.toggle('visible', text !== '');
    this.connection.classList.toggle('bad', status === 'reconnecting' || status === 'closed');
  }

  private chip(result: RoundResult, onSelect: (nonce: number) => void): HTMLButtonElement {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = `chip ${band(result.crashX100)}`;
    chip.textContent = formatMultiplier(result.crashX100);
    chip.title = t('hud.roundTitle', { nonce: result.nonce });
    // The nonce is kept on the element so a language change can retitle a strip
    // of thirty chips without rebuilding it and losing the scroll position.
    chip.dataset['nonce'] = String(result.nonce);
    chip.addEventListener('click', () => onSelect(result.nonce));
    return chip;
  }

  private retitleHistory(): void {
    if (!this.onSelectRound) return;
    for (const chip of this.history.querySelectorAll<HTMLElement>('.chip')) {
      const nonce = Number(chip.dataset['nonce']);
      if (Number.isFinite(nonce)) chip.title = t('hud.roundTitle', { nonce });
    }
  }
}

/**
 * Which of the three colour bands a result falls in. Exported because the
 * history panel behind the "···" button colours its cells by the same rule, and
 * two rules would mean the same 2.00x reading gold in one place and grey in the
 * other.
 */
export function band(x100: number): 'low' | 'mid' | 'high' {
  if (x100 >= 1000) return 'high';
  if (x100 >= 200) return 'mid';
  return 'low';
}

function required(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`hud: #${id} is missing from the page`);
  return el;
}

function child(parent: HTMLElement, selector: string): HTMLElement {
  const el = parent.querySelector<HTMLElement>(selector);
  if (!el) throw new Error(`hud: ${selector} is missing from #${parent.id}`);
  return el;
}
